/**
 * Company Create Modal — form for creating a new company.
 */

import { useState } from 'react'
import { useCRM } from '@/hooks/useCRM'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  useToast,
} from '@/components/ui'

interface CompanyCreateModalProps {
  onClose: () => void
  onCreated: () => void
}

export function CompanyCreateModal({ onClose, onCreated }: CompanyCreateModalProps) {
  const { createCompany } = useCRM()
  const { addToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '', domain: '', industry: '', phone: '',
    address: '', city: '', country: '', notes: '',
  })

  const handleSubmit = async () => {
    if (!formData.name.trim()) return
    setIsSubmitting(true)
    const result = await createCompany({
      name: formData.name.trim(),
      domain: formData.domain.trim() || undefined,
      industry: formData.industry.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      address: formData.address.trim() || undefined,
      city: formData.city.trim() || undefined,
      country: formData.country.trim() || undefined,
      notes: formData.notes.trim() || undefined,
    })
    setIsSubmitting(false)
    if (result) {
      addToast({ type: 'success', title: 'Company created' })
      onCreated()
    } else {
      addToast({ type: 'error', title: 'Failed to create company', description: 'Check your connection and try again.' })
    }
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent
        size="sm"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <ModalHeader><ModalTitle>New Company</ModalTitle></ModalHeader>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
        <ModalBody className="space-y-3">
          <Input
            label="Company Name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Acme Corp"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Domain"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="acme.com"
            />
            <Input
              label="Industry"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              placeholder="e.g., Technology"
            />
          </div>
          <Input
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="City"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            />
            <Input
              label="Country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              placeholder="e.g., US"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="resize-none"
              rows={2}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!formData.name.trim()} isLoading={isSubmitting}>Create Company</Button>
        </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
